// Email service for sending notifications
// This uses EmailJS for client-side email sending
// Alternative: Use a backend service like SendGrid, Mailgun, or Supabase Edge Functions

import emailjs from '@emailjs/browser';
import { supabase } from '../lib/supabase';

// Initialize EmailJS with your public key
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

// Initialize EmailJS
if (EMAILJS_PUBLIC_KEY) {
  emailjs.init(EMAILJS_PUBLIC_KEY);
}

export const emailService = {
  // Send contact form notification
  async sendContactNotification(formData) {
    // Try Supabase Edge Function first (if available)
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: 'info@seyardsolutions.com',
          subject: `New Contact Form - ${formData.service || 'General Inquiry'}`,
          html: `
            <h2>New Contact Form Submission</h2>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Phone:</strong> ${formData.phone || 'Not provided'}</p>
            <p><strong>Service:</strong> ${formData.service || 'Not specified'}</p>
            <p><strong>Preferred Date:</strong> ${formData.preferredDate || 'Not specified'}</p>
            <p><strong>Preferred Time:</strong> ${formData.preferredTime || 'Not specified'}</p>
            <p><strong>Message:</strong> ${formData.message || 'No message'}</p>
            <hr>
            <p>Submitted at: ${new Date().toLocaleString()}</p>
          `,
          text: `New contact from ${formData.name} (${formData.email}): ${formData.message}`
        }
      });

      if (!error && data?.success) {
        console.log('Email sent via Supabase Edge Function');
        return { success: true, method: 'supabase' };
      }
    } catch (err) {
      console.warn('Supabase email function not available:', err);
    }

    // Fallback to EmailJS if configured
    if (!EMAILJS_PUBLIC_KEY || !EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID) {
      console.error('⚠️ EMAIL NOT CONFIGURED! Set up EmailJS or Resend to receive notifications.');
      console.log('📧 Would have sent email for:', formData);
      console.log('📋 Quick fix: Follow instructions in EMAIL_SETUP_GUIDE.md');
      return { 
        success: false, 
        error: 'Email service not configured. Check EMAIL_SETUP_GUIDE.md for setup instructions.' 
      };
    }

    try {
      const templateParams = {
        to_email: 'info@seyardsolutions.com',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        service: formData.service || 'General Inquiry',
        preferred_date: formData.preferredDate || 'Not specified',
        preferred_time: formData.preferredTime || 'Not specified',
        message: formData.message || 'No additional message',
        subject: `New Contact Form Submission - ${formData.service || 'General Inquiry'}`,
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );

      return { success: true, response };
    } catch (error) {
      console.error('Email sending failed:', error);
      return { success: false, error: error.message };
    }
  },

  // Send lead notification
  async sendLeadNotification(leadData) {
    if (!EMAILJS_PUBLIC_KEY || !EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID) {
      console.warn('EmailJS not configured. Lead notification not sent.');
      return { success: false, error: 'Email service not configured' };
    }

    try {
      const vehicleInfo = leadData.vehicleMake && leadData.vehicleModel 
        ? `${leadData.vehicleYear} ${leadData.vehicleMake} ${leadData.vehicleModel}`
        : 'Not specified';

      const budgetInfo = leadData.budgetMin && leadData.budgetMax
        ? `$${leadData.budgetMin.toLocaleString()} - $${leadData.budgetMax.toLocaleString()}`
        : 'Not specified';

      const templateParams = {
        to_email: 'info@seyardsolutions.com',
        from_name: `${leadData.firstName} ${leadData.lastName}`,
        from_email: leadData.email,
        phone: leadData.phone,
        lead_type: leadData.leadType.replace('_', ' ').toUpperCase(),
        vehicle_interest: vehicleInfo,
        budget_range: budgetInfo,
        financing_needed: leadData.financingNeeded ? 'Yes' : 'No',
        preferred_contact: leadData.preferredContactMethod,
        contact_time: leadData.preferredContactTime || 'Not specified',
        message: leadData.message || 'No additional message',
        subject: `New ${leadData.leadType.replace('_', ' ')} Lead - ${leadData.firstName} ${leadData.lastName}`,
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );

      return { success: true, response };
    } catch (error) {
      console.error('Lead email sending failed:', error);
      return { success: false, error: error.message };
    }
  },

  // Send deal documents via email
  async sendDealDocuments(dealData, customerEmail) {
    try {
      // Use Supabase Edge Function for document sending
      const { data, error } = await supabase.functions.invoke('send-deal-documents', {
        body: {
          to: customerEmail || dealData.email,
          customerName: dealData.name || 'Customer',
          dealId: dealData.id,
          vehicleInfo: {
            year: dealData.vehicleYear,
            make: dealData.vehicleMake, 
            model: dealData.vehicleModel,
            vin: dealData.vehicleVin,
            price: dealData.salePrice
          },
          documents: dealData.documents || [],
          dealerInfo: {
            name: 'ShedX',
            phone: '',
            email: 'info@seyardsolutions.com',
            address: ',  '
          }
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Failed to send deal documents:', error);
      
      // Fallback to EmailJS if available
      if (EMAILJS_PUBLIC_KEY && EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID) {
        try {
          const templateParams = {
            to_email: customerEmail || dealData.email,
            customer_name: dealData.name || 'Valued Customer',
            deal_id: dealData.id,
            vehicle: `${dealData.vehicleYear} ${dealData.vehicleMake} ${dealData.vehicleModel}`,
            vin: dealData.vehicleVin,
            sale_price: `$${(dealData.salePrice || 0).toLocaleString()}`,
            documents_link: `https://homedepotshed.com/deals/${dealData.id}/documents`,
            subject: `Your Deal Documents - ${dealData.vehicleYear} ${dealData.vehicleMake} ${dealData.vehicleModel}`,
            message: 'Your deal documents are ready for review. Please click the link below to access them.'
          };

          const response = await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID, 
            templateParams
          );
          
          return { success: true, response, method: 'emailjs' };
        } catch (emailjsError) {
          console.error('EmailJS fallback also failed:', emailjsError);
          return { 
            success: false, 
            error: 'Unable to send emails. Please configure email service.',
            details: error.message
          };
        }
      }
      
      return { 
        success: false, 
        error: 'Email service not configured. Please set up EmailJS or Supabase Edge Functions.',
        details: error.message
      };
    }
  },

  // Send auto-reply to customer
  async sendAutoReply(customerEmail, customerName, inquiryType = 'inquiry') {
    // This would use a different template for customer auto-replies
    const CUSTOMER_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_CUSTOMER_TEMPLATE_ID;
    
    if (!CUSTOMER_TEMPLATE_ID) {
      console.warn('Customer auto-reply template not configured.');
      return { success: false, error: 'Auto-reply not configured' };
    }

    try {
      const templateParams = {
        to_email: customerEmail,
        customer_name: customerName,
        inquiry_type: inquiryType,
        business_name: 'ShedX',
        business_phone: '',
        business_email: 'info@seyardsolutions.com',
        business_address: ',  ',
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        CUSTOMER_TEMPLATE_ID,
        templateParams
      );

      return { success: true, response };
    } catch (error) {
      console.error('Auto-reply sending failed:', error);
      return { success: false, error: error.message };
    }
  }
};

export default emailService;
